import { ErrorSidePanel } from '@/components/errors';
import { useForm } from '@deadcow-enterprises/react-forms';
import {
  ImageProfile,
  logger,
  mage,
  templates,
  useI18n,
  widgets,
  type SkinPageConfig,
} from '@nosinovacao/nosid-mfe-common';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import z from 'zod';
import { useGetProfile } from '../-hooks/profile.hook';
import { PinValidationLight, PinValidationWooDark, PinValidationWoo } from './-skins';
import { type PinValidationProps } from './-model/props';

export const Route = createFileRoute(
  '/(tv)/tv/$id/(tv-profiles)/_sheet/profiles/$profileId/update/pin-validation/',
)({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
});

function RouteComponent() {
  const { id, profileId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { data: profile } = useGetProfile(id, profileId);
  const { t } = useI18n();

  const expectedPin = profile?.Pin ?? '';

  const form = useForm<{ pin: string }>(
    {
      pin: {
        value: '',
        id: 'profile-pin-validation',
        validator: z
          .string()
          .length(
            widgets.PinCodeLength,
            t('TvProfiles.PinValidation.Errors.PinRequired').toString(),
          ),
      },
    },
    { onError: logger.logFormErrors.bind(logger) },
  );

  const goToUpdate = (pin: string) => {
    void navigate({
      to: '/tv/$id/profiles/$profileId/update',
      params: { id, profileId },
      state: { pin },
    });
  };

  const focusFirstSlot = () => {
    requestAnimationFrame(() => {
      document.getElementById('profile-pin-validation')?.focus();
    });
  };

  const handleSubmit = ({ pin }: Readonly<{ pin: string }>) => {
    if (pin === expectedPin) {
      goToUpdate(pin);
    } else {
      form.addError('pin', t('TvProfiles.PinValidation.Errors.InvalidPin').toString());
      form.setValue('pin', '');
      focusFirstSlot();
    }
  };

  const handlePinChange = (value: string) => {
    form.setValue('pin', value);
    if (value.length === widgets.PinCodeLength) {
      form.updateValidity();
      if (value === expectedPin) {
        goToUpdate(value);
      } else {
        form.addError('pin', t('TvProfiles.PinValidation.Errors.InvalidPin').toString());
        form.setValue('pin', '');
        focusFirstSlot();
      }
    }
  };

  if (!profile || !profile.Pin) {
    return <RedirectToUpdate id={id} profileId={profileId} />;
  }

  const props: PinValidationProps = {
    form,
    handleSubmit,
    handlePinChange,
    avatarUri: mage.getImage(profile.Avatar?.ImageUri, ImageProfile.Avatar),
    nickname: profile.Nickname,
    onContactVerification: () => {
      void navigate({
        to: '/tv/$id/profiles/$profileId/update/contact-selection',
        params: { id, profileId },
      });
    },
  };

  return templates.getSkinSelector(skinConfig(props));
}

function skinConfig(props: PinValidationProps): SkinPageConfig {
  return {
    skins: [
      {
        selector: 'isWooTvTemplate',
        component: <PinValidationWooDark {...props} />,
      },
      {
        selector: 'isWooTemplate',
        component: <PinValidationWoo {...props} />,
      },
    ],
    defaultValue: <PinValidationLight {...props} />,
  };
}

function RedirectToUpdate({ id, profileId }: Readonly<{ id: string; profileId: string }>) {
  const navigate = Route.useNavigate();
  useEffect(() => {
    void navigate({
      to: '/tv/$id/profiles/$profileId/update',
      params: { id, profileId },
    });
  }, [navigate, id, profileId]);
  return null;
}
